/**
 * 预测快照（Prediction Snapshot）
 *
 * 回填录取结果时，把「当前线上模型对该考生分数的预测」压成一份紧凑 JSON 一并上送。
 * 明年校准时，可直接用快照里的 point 概率与真实录取结果对照，
 * 计算 Brier 分 / 命中率 / 分校校准曲线 —— 无需用新代码重算旧版预测，
 * 也就避免了「换了模型后无法复盘旧模型表现」的问题。
 *
 * 设计原则：
 *  - 只取校准 / 复盘真正需要的字段，绝不塞入整张 policy / preference 矩阵（体积可控）。
 *  - 概率四舍五入到 4 位小数，进一步压体积、避免无意义精度。
 *  - 带 schemaVersion（v），未来字段演进时离线脚本可分版本解析。
 */
import type { CalcResult, ConservativeLevel, ProbabilityTag, SchoolName } from '@/domain/types'

export interface PredictionSnapshotSchool {
  school: SchoolName
  point: number
  lower: number
  upper: number
  tag: ProbabilityTag
  structuralCertain: boolean
}

export interface PredictionSnapshot {
  v: 1
  year: number
  score: number
  model: 'ratio' | 'bayesian'
  conservativeLevel: ConservativeLevel
  densityMethod: string
  position: {
    inTop300: boolean
    band: { upper: number; lower: number } | null
    cumulativePosition: number
    estimatedRank: { min: number; max: number }
  }
  schools: PredictionSnapshotSchool[]
  suggestion: { first?: SchoolName; second?: SchoolName }
}

const round4 = (x: number): number => Math.round(x * 10000) / 10000

export function buildPredictionSnapshot(result: CalcResult): PredictionSnapshot {
  const { input, position, schools, suggestion } = result
  return {
    v: 1,
    year: input.policy.year,
    score: input.score,
    model: input.probabilityModel ?? 'bayesian',
    conservativeLevel: input.conservativeLevel ?? 'normal',
    densityMethod: input.densityMethod ?? 'whittaker',
    position: {
      inTop300: position.inTop300,
      band: position.band ? { upper: position.band.upper, lower: position.band.lower } : null,
      cumulativePosition: round4(position.cumulativePosition),
      estimatedRank: {
        min: position.estimatedRankRange.min,
        max: position.estimatedRankRange.max
      }
    },
    schools: schools.map((s) => ({
      school: s.school,
      point: round4(s.point),
      lower: round4(s.lower),
      upper: round4(s.upper),
      tag: s.tag,
      structuralCertain: s.structuralCertain
    })),
    suggestion: { first: suggestion.first, second: suggestion.second }
  }
}
